import moment from "moment";

interface MinimalClaim {
  date_of_service?: string;
}

interface MinimalMember {
  member: {
    effective_date?: string;
    termination_date?: string;
    id?: string | number;
  };
  group: {
    start_date?: string;
    end_date?: string;
  };
}

/**
 * When we redirect users to the test claim page, we want to default the member to the one that actually
 * matches the claim's date of service. You can view this claim from the inactive or not-applicable
 * member, but the test adjudication should default to being done with the member that was active at the time
 *
 * @param claim - The claim object containing the date of service.
 * @param memberList - The list of members to search through.
 * @returns The ID of the matching member as a string, or an empty string if no match is found.
 */
export const findMemberIdBasedOnDOS = (
  memberList: readonly MinimalMember[],
  claim: MinimalClaim,
): string => {
  if (!claim.date_of_service) {
    return "";
  }
  const dateOfService = moment(claim.date_of_service).utc();

  const foundMemberForDateRange = memberList.find((member) => {
    if (
      !member.member.effective_date ||
      !member.member.termination_date
    ) {
      return false;
    }

    /**
     * The effective and termination dates come from the _most recent_ member version, which
     * is the only version the member/list endpoint returns. It's technically possible that the
     * most recent member version might not cover the "full" date range of that member with
     * that plan (example: a new member version is added with an effective of 10/1 when the
     * previous member version had an effective of 1/1 and termination of 12/31). In practice,
     * this should be rare enough that we can ignore it for now.
     */
    const effectiveDate = moment(member.member.effective_date);
    const terminationDate = moment(member.member.termination_date);

    return (
      dateOfService.isBetween(effectiveDate, terminationDate, undefined, "[]")
    );
  });

  return foundMemberForDateRange && foundMemberForDateRange.member.id != null
    ? foundMemberForDateRange.member.id.toString()
    : "";
};
